/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { render, VNode, createContext } from 'preact';
import renderToString from 'preact-render-to-string';
import { Container, VComponent } from '@adobe-commerce/elsie/lib';
import { Signal, signal } from '@adobe-commerce/elsie/lib/signals';

export const SlotQueueContext = createContext<Signal<Set<string>> | null>(null);

type RenderAPI = {
  remove: () => void;
  setProps: (cb: (prev: any) => any) => void;
};

/**
 * The `Render` class provides methods to render and unmount components, as well as to render components to a string.
 * @class
 *
 * @property {Function} render - Renders a component to a root element.
 * @property {Function} toString - Renders a component to a string.
 */
export class Render {
  private _provider: VNode<any>;

  constructor(provider: VNode<any>) {
    this._provider = provider;
  }

  /**
   * Renders a container to a root element.
   * @param Container - The container to render.
   * @param props - The container parameters.
   * @returns A function to render the component to a root element.
   */
  render<T>(Component: Container<T>, props: T) {
    /**
     * Renders a component to a root element.
     * @param rootElement - The root element to render the component to.
     * @returns A promise that resolves to an object with methods to control the rendered component.
     */
    return async (rootElement: HTMLElement): Promise<RenderAPI> => {
      if (!Component) throw new Error('Component is not defined');
      if (!rootElement) throw new Error('Root element is not defined');

      const initialData = (await Component.getInitialData?.(props)) ?? {};

      const state = signal<T>({ ...props });

      const queue = signal<Set<string>>(new Set());

      const provider = this._provider;

      const Root = ({ next }: { next: Signal<T> }) => {
        return (
          <SlotQueueContext.Provider value={queue}>
            <VComponent node={provider} {...provider.props}>
              <Component {...next.value} initialData={initialData} />
            </VComponent>
          </SlotQueueContext.Provider>
        );
      };

      // clear the root element
      rootElement.innerHTML = '';

      // clone the root element to initialize rendering on the background
      const root = document.createElement('div');

      // apply base design tokens and global styles to the root element
      rootElement.classList.add('dropin-design');
      rootElement.setAttribute('data-dropin-container', Component.name);

      // store the virtual root element
      (rootElement as any).__rootElement = root;

      render(<Root next={state} />, root);

      // API object to control the rendered component
      const API: RenderAPI = {
        remove: () => {
          render(null, root);
        },
        setProps: (cb: (prev: T) => T) => {
          const next = cb(state.peek());
          state.value = next;
        },
      };

      // wait for all slots to be resolved
      return new Promise((resolve) => {
        queue.subscribe((pending) => {
          if (pending.size === 0) {
            // apply base design tokens and global styles to the root element
            rootElement.classList.add('dropin-design');

            // append the rendered component to the DOM only when all slots are resolved
            rootElement.appendChild(root.firstChild ?? root);

            return resolve(API);
          }
        });
      });
    };
  }

  /**
   * Unmounts a container from a root element.
   * @param rootElement - The root element to unmount the container from.
   */
  static unmount(rootElement: HTMLElement) {
    const root = (rootElement as any)?.__rootElement;
    if (!root) throw new Error('Root element is not defined');
    render(null, root);
  }

  /**
   * UnRenders a component from a root element.
   * @param rootElement - The root element to unmount the component from.
   * @deprecated Use `remove` method from the returned object of the `mount` method instead or `unmount` method from the `Render` class.
   */
  unmount(rootElement: HTMLElement) {
    if (!rootElement) throw new Error('Root element is not defined');
    rootElement.firstChild?.remove();  
  }

  /**
   * Renders a component to a string.
   * @param Component - The component to render.
   * @param props - The component props.
   * @param options - Optional rendering options.
   */
  async toString<T>(Component: Container<T>, props: T, options?: T) {
    if (!Component) throw new Error('Component is not defined');

    const initialData = (await Component.getInitialData?.(props)) ?? {};

    return renderToString(
      <VComponent node={this._provider} {...this._provider.props}>
        <Component {...props} initialData={initialData} />
      </VComponent>,
      {},
      { ...options }
    );
  }
}